import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Search, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ListTools } from "@/components/ListToolbar";
import { DataList, DetailPanel, DetailPlaceholder, type Column } from "@/components/DataList";
import { Badge } from "@/components/ui";
import { listSerials, serialDetailQuery, serialListQuery, type SerialRow } from "@/features/serials/api";
import { SerialPanel } from "@/features/serials/SerialPanel";
import { cn } from "@/lib/cn";
import { EXPORT_PAGE, type ExportColumn } from "@/lib/exportXlsx";
import { serialListColumns } from "@patronix/domain/list";
import { listView, noView, viewQuery, viewSearch, type ViewSearch } from "@/lib/listView";

// Serial numbers (master data): every unit of the tenant — where it is now,
// its lot and project — with the trace in the detail. Serials are created by
// receipts, production reports, adjustments and counts, never typed in, so
// the list is export-only (ADR-034: computed by postings).

interface Search extends ViewSearch {
  q: string;
  inStock: boolean;
  serial?: string | undefined;
}

export const Route = createFileRoute("/_app/master-data/serials")({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const s: Search = {
      q: typeof raw.q === "string" ? raw.q.slice(0, 40) : "",
      inStock: raw.inStock === true || raw.inStock === "true",
    };
    if (typeof raw.serial === "string" && raw.serial) s.serial = raw.serial;
    return { ...s, ...viewSearch(serialListColumns, raw) };
  },
  component: SerialsPage,
});

function SerialsPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();

  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const view = viewQuery(serialListColumns, search);
  const params = {
    q: search.q,
    ...view,
    filters: [...(view.filters ?? []), ...(search.inStock ? [{ c: "status", o: "in" as const, v: ["in_stock"] }] : [])],
  };
  const query = useInfiniteQuery(serialListQuery(params));

  const columns: Column<SerialRow>[] = [
    {
      key: "serial",
      header: t("serialList.colSerial"),
      width: "minmax(140px,1fr)",
      cell: (r, selected) => <span className={cn("font-mono text-xs", selected && "font-semibold text-accent-ink")}>{r.serial_no}</span>,
    },
    {
      key: "item",
      header: t("serialList.colItem"),
      width: "minmax(200px,2fr)",
      cell: (r) => (
        <span className="truncate">
          <span className="font-mono text-xs">{r.item_code}</span> <span className="text-ink-muted">{r.item_description}</span>
        </span>
      ),
    },
    {
      key: "status",
      header: t("serialList.colStatus"),
      width: "minmax(100px,0.6fr)",
      cell: (r) => <Badge tone={r.status === "in_stock" ? "success" : "neutral"}>{t(`serialList.status.${r.status}`)}</Badge>,
    },
    {
      key: "location",
      header: t("serialList.colWhere"),
      width: "minmax(140px,1fr)",
      cell: (r) =>
        r.warehouse_code ? (
          <span className="font-mono text-xs">
            {r.warehouse_code} · {r.location_code}
          </span>
        ) : (
          <span className="text-ink-muted">—</span>
        ),
    },
    {
      key: "lot",
      header: t("serialList.colLot"),
      width: "minmax(110px,0.8fr)",
      cell: (r) => (r.lot_code ? <span className="font-mono text-xs">{r.lot_code}</span> : <span className="text-ink-muted">—</span>),
    },
  ];

  const exportColumns: ExportColumn<SerialRow>[] = [
    { header: t("serialList.colSerial"), kind: "code", value: (r) => r.serial_no },
    { header: t("serialList.colItem"), kind: "code", value: (r) => r.item_code },
    { header: t("serialList.colDescription"), kind: "text", value: (r) => r.item_description },
    { header: t("serialList.colStatus"), kind: "text", value: (r) => t(`serialList.status.${r.status}`) },
    { header: t("serialList.colWarehouse"), kind: "code", value: (r) => r.warehouse_code },
    { header: t("serialList.colLocation"), kind: "code", value: (r) => r.location_code },
    { header: t("serialList.colLot"), kind: "code", value: (r) => r.lot_code },
    { header: t("serialList.colProject"), kind: "code", value: (r) => r.project_code },
  ];

  const listTools: ListTools<SerialRow> = {
    id: "serials",
    title: t("serialList.title"),
    export: {
      fileBase: "serial-numbers",
      sheet: t("serialList.title"),
      columns: exportColumns,
      fetchPage: (cursor, signal) => listSerials(params, cursor, signal, EXPORT_PAGE),
    },
  };

  return (
    <div className="flex min-h-0 grow flex-col gap-4">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.masterData.title")} <span aria-hidden="true">/</span> {t("nav.masterData.serials")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("serialList.title")}</h1>
        </div>
      </header>

      <Toolbar search={search} setSearch={setSearch} />

      <div className="flex min-h-0 grow gap-4">
        <DataList
          label={t("serialList.listLabel")}
          columns={columns}
          query={query}
          rowId={(r) => r.id}
          selectedId={search.serial}
          onSelect={(r) => setSearch({ serial: r.id })}
          onClear={() => setSearch({ serial: undefined })}
          onPrefetch={(r) => void queryClient.prefetchQuery(serialDetailQuery(r.id))}
          emptyTitle={t("serialList.emptyTitle")}
          emptyBody={t("serialList.emptyBody")}
          emptyAction={
            <button
              type="button"
              onClick={() => setSearch({ q: "", inStock: false, serial: undefined, ...noView })}
              className="h-9 rounded-xl border border-border px-3.5 text-[13px] font-medium hover:bg-surface-hover"
            >
              {t("list.clearFilters")}
            </button>
          }
          minWidth={680}
          tools={listTools}
          view={listView(serialListColumns, search, setSearch)}
        />
        <DetailPanel
          label={t("serialList.detailLabel")}
          open={!!search.serial}
          placeholder={<DetailPlaceholder title={t("serialList.selectPrompt")} hint={t("serialList.selectPromptHint")} />}
        >
          {search.serial && <SerialPanel key={search.serial} serialId={search.serial} onClose={() => setSearch({ serial: undefined })} />}
        </DetailPanel>
      </div>
    </div>
  );
}

function Toolbar({ search, setSearch }: { search: Search; setSearch: (p: Partial<Search>) => void }) {
  const { t } = useTranslation();
  const [text, setText] = useState(search.q);

  useEffect(() => {
    if (text === search.q) return;
    const id = setTimeout(() => setSearch({ q: text }), 200);
    return () => clearTimeout(id);
  }, [text, search.q, setSearch]);

  const chip = (active: boolean) =>
    cn(
      "h-8 rounded-full border px-3 text-[13px] transition-colors",
      active ? "border-ink bg-ink font-medium text-surface" : "border-border bg-surface text-ink hover:bg-surface-hover",
    );

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button type="button" aria-pressed={search.inStock} onClick={() => setSearch({ inStock: !search.inStock })} className={chip(search.inStock)}>
        {t("serialList.filterInStock")}
      </button>
      <div className="grow" />
      <label className="flex h-9 min-w-[240px] items-center gap-2 rounded-xl border border-border bg-surface px-3 text-ink-muted focus-within:outline-2 focus-within:outline-focus">
        <Search size={15} aria-hidden="true" />
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("serialList.searchPlaceholder")}
          aria-label={t("serialList.searchPlaceholder")}
          data-list-search
          className="min-w-0 grow bg-transparent font-mono text-[13px] text-ink uppercase outline-none placeholder:font-sans placeholder:normal-case placeholder:text-ink-muted"
        />
        {text && (
          <button type="button" aria-label={t("list.clearFilters")} onClick={() => setText("")} className="text-ink-muted">
            <X size={14} />
          </button>
        )}
      </label>
    </div>
  );
}
